import {
  getOwnerDocument,
  useLayoutEffect,
  useObjectRef,
  useResizeObserver,
  useViewportSize,
} from '@react-aria/utils';
import type { DOMProps } from '@react-types/shared';
import { debounce, once } from 'lodash-es';
import {
  AnimatePresence,
  motion,
  useMotionTemplate,
  useMotionValue,
  useTransform,
} from 'motion/react';
import type { ForwardedRef, RefObject } from 'react';
import { forwardRef, useContext, useMemo, useRef, useState } from 'react';
import type { ModalOverlayProps } from 'react-aria-components';
import {
  composeRenderProps,
  Modal,
  ModalOverlay,
  ModalContext,
  OverlayTriggerStateContext,
} from 'react-aria-components';
import type { OverlayTriggerProps, OverlayTriggerState } from 'react-stately';
import { useOverlayTriggerState } from 'react-stately';

import { remap } from '@infinex-private/ts-utils/number';
import type { UnsafeStyleProps } from '@infinex/ui-styles';
import { cn, useContextProps } from '@infinex/ui-styles';
import { getComputedStyle, isKeyboardInput } from '@infinex/ui-utils/dom';

import {
  IOS_26_BOTTOM_CHROME_HEIGHT,
  isSafari26,
  RacOverlayVars,
  TRAY_INSET,
  TRAY_INSET_TOP,
  TrayVars,
} from './constants';
import type { ModalPrimitiveProps } from './ModalPrimitive';
import { getModalLayoutClasses } from './ModalPrimitive';
import { useScrollIntervention } from './useScrollIntervention';

import { SurfaceContext } from '../surface';

const MotionModal = motion.create(Modal);
const MotionModalOverlay = motion.create(ModalOverlay);

const DRAG_ELASTIC = { top: 0.1, bottom: 1 }; // default resistance (0 = no movement, 1 = full movement)
const PHYSICAL_PROPERTIES = { mass: 0.8, damping: 26, stiffness: 280 };

export type TrayPrimitiveProps = {
  // other props?
} & Omit<ModalOverlayProps, 'onAnimationStart' | 'className' | 'style'> &
  Pick<ModalPrimitiveProps, 'onOpenComplete' | 'onCloseComplete'> &
  DOMProps &
  UnsafeStyleProps;

/**
 * Styled tray overlay. Prefer `<Dialog mobileType="tray" />` in most cases.
 */
export const TrayPrimitive = forwardRef(function TrayPrimitive(
  props: TrayPrimitiveProps,
  forwardedRef: ForwardedRef<HTMLDivElement>
) {
  [props, forwardedRef] = useContextProps(props, forwardedRef, ModalContext);

  const domRef = useObjectRef(forwardedRef);
  const state = useTrayState(props);

  return (
    <AnimatePresence>
      {state.isOpen && (
        <TrayInner key="tray" trayRef={domRef} state={state} {...props} />
      )}
    </AnimatePresence>
  );
});

type TrayInnerProps = {
  trayRef: RefObject<HTMLDivElement | null>;
  state: OverlayTriggerState;
} & TrayPrimitiveProps;

function TrayInner(props: TrayInnerProps) {
  const {
    className,
    style,
    state,
    trayRef,
    isOpen: _unusedIsOpen,
    onOpenChange,
    ...otherProps
  } = props;

  useScrollIntervention(trayRef);

  const [hasAnimated, setHasAnimated] = useState(false);
  const [isTouchSafe, setTouchSafe] = useState(false);
  const [isDragging, setDragging] = useState(false);
  const [isStable, setStable] = useState(false);

  const overlayRef = useRef<HTMLDivElement>(null);

  // the `onAnimationComplete` event is called after all sorts of animations
  // e.g. elastic snap back to the initial position after dragging up. these
  // guards ensure consumer handlers are only called when appropriate
  const onOpenComplete = props.onOpenComplete && once(props.onOpenComplete);
  const onCloseComplete = props.onCloseComplete && once(props.onCloseComplete);

  const { height: viewportHeight } = useViewportSize();
  const modalLayoutClasses = getModalLayoutClasses();

  // block height observation while dragging or animating
  const height = useHeightObserver(trayRef, { isDisabled: !isStable });
  const maxHeight = useMemo(
    () =>
      getComputedNumber(overlayRef.current, TrayVars.get('maxHeight')) ??
      viewportHeight - TRAY_INSET - TRAY_INSET_TOP,
    [viewportHeight]
  );
  const maxY = height ?? maxHeight;
  const topOffset = useMemo(() => {
    const el = trayRef.current;
    if (!el) return maxY;
    const rect = el.getBoundingClientRect();
    return window.innerHeight - rect.top;
  }, [maxY, trayRef]);

  const dragY = useMotionValue(0);
  // To create the illusion that the tray is anchored to the bottom of the
  // screen when dragging up:
  // 1. increase the height of an element below the content
  // 2. apply a negative margin to offset the drag distance
  const stretchHeight = useTransform(dragY, (value) => {
    // ignore the initial animation
    if (!hasAnimated) return 0;
    // only stretch when dragging up
    if (value < 0) return Math.abs(value);

    return 0;
  });
  const stretchOffset = useTransform(stretchHeight, (value) => -value); // [2]
  const overlayOpacity = useTransform(
    dragY,
    [-maxY * DRAG_ELASTIC.top, 0, maxY],
    [0.8, 0.6, 0]
  );
  const overlayBg = useMotionTemplate`rgba(0, 0, 0, ${overlayOpacity})`;

  return (
    <>
      <MotionModalOverlay
        layoutRoot
        ref={overlayRef}
        // force open: let `AnimatePresence` handle enter/exit
        isOpen
        onOpenChange={onOpenChange}
        // circumvents an issue where `AnimatePresence` doesn't "release" the
        // exiting component when close is called before the open animation
        // completes. the failed unmount is an awful experience: it looks like the
        // dialog is gone but it's just out of view, meanwhile the app is
        // oblivious to user gestures.
        isDismissable={isTouchSafe}
        isKeyboardDismissDisabled={!isTouchSafe}
        className={modalLayoutClasses.overlay}
        style={{
          backgroundColor: overlayBg,
          ...TrayVars.apply((vars) => ({
            inset: TRAY_INSET,
            insetBottom: `max(${vars.inset}, env(safe-area-inset-bottom))`,
            insetTop: `max(${TRAY_INSET_TOP}px, env(safe-area-inset-top))`,
            maxHeight: `calc(${RacOverlayVars.viewportHeight} - ${vars.insetBottom} - ${vars.insetTop})`,
            maxWidth: `calc(clamp(320px, 100vmin, 540px) - ${vars.inset} * 2)`,
            padding: 24,
          })),
        }}
      >
        <div
          className={cn(
            modalLayoutClasses.wrapper,
            TrayVars.tw('pb-(--Tray-inset-bottom)', 'px-(--Tray-inset)'),
            'flex flex-col justify-end'
          )}
        >
          <MotionModal
            {...otherProps}
            ref={trayRef}
            initial="hidden"
            animate="visible"
            exit="unmount"
            variants={{
              hidden: { y: maxY },
              visible: { y: 0 },
              unmount: { y: getUnmountY(topOffset) },
            }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{
              bottom: DRAG_ELASTIC.bottom,
              // increase drag-up resistance when the tray is taller
              top: remap(maxY, [0, viewportHeight], [DRAG_ELASTIC.top, 0]),
            }}
            transition={{ type: 'spring', ...PHYSICAL_PROPERTIES }}
            dragTransition={{
              bounceDamping: PHYSICAL_PROPERTIES.damping,
              bounceStiffness: PHYSICAL_PROPERTIES.stiffness,
            }}
            onUpdate={(latest) => {
              if (isDragging) {
                setStable(false);
                return;
              }

              // just for TS, `latest.y` is always a number
              const latestY = Number(latest.y);

              // the spring animation oscillates around `0` for a while before
              // settling. to avoid unnecessary state updates we consider stability
              // within range (decay makes this pretty safe) rather than at
              // "exactly" zero
              setStable(latestY > -1 && latestY < 1);
            }}
            onAnimationComplete={(variant) => {
              const isVisible = variant === 'visible';
              const handler = isVisible ? onOpenComplete : onCloseComplete;
              handler?.();

              setTouchSafe(isVisible);
              setHasAnimated(true);
            }}
            onDragStart={(event) => {
              setDragging(true);

              // cancel react-aria press event when dragging starts
              if (event.target instanceof Element) {
                getOwnerDocument(event.target).dispatchEvent(
                  new PointerEvent('pointercancel')
                );
              }
            }}
            onDragEnd={(event, { offset, velocity }) => {
              setDragging(false);

              if (event.type.includes('cancel')) return;

              // close if released below 1/2 tray height. however, when there's
              // enough downward velocity we don't care about distance
              if (offset.y + velocity.y > maxY * 0.5) {
                state.close();
              }
            }}
            className={cn(
              'relative mx-auto w-full will-change-transform',
              'bg-surface rounded-large shadow-lg',
              // hairline border looks more refined on high-res displays
              '2dppx:[--res-px:0.5px] 3dppx:[--res-px:0.33px] 4dppx:[--res-px:0.25px] [--res-px:1px]',
              'border-secondary border-[length:--res-px]',
              TrayVars.tw(
                'max-w-(--Tray-max-width)',
                'max-h-(--Tray-max-height)'
              ),
              className
            )}
            style={{ y: dragY, bottom: stretchOffset, ...style }}
          >
            {composeRenderProps(props.children, (children) => (
              <>
                <DragAffordance />

                {/*
              we need to set the correct surface layer for descendant components
              because of the "surface" background applied to the parent above
            */}
                <SurfaceContext.Provider value={1}>
                  {children}
                </SurfaceContext.Provider>

                <motion.div
                  layout="size"
                  style={{ height: stretchHeight }}
                  transition={{ type: 'spring', ...PHYSICAL_PROPERTIES }}
                />
              </>
            ))}
          </MotionModal>
        </div>
      </MotionModalOverlay>
    </>
  );
}

// Utils -----------------------------------------------------------------------

export function getUnmountY(distance: number) {
  const diff = window.innerHeight - document.documentElement.clientHeight;
  const extra = isSafari26() ? IOS_26_BOTTOM_CHROME_HEIGHT : diff;

  return distance + extra;
}

function getComputedNumber(
  element: HTMLElement | null | undefined,
  property: string
) {
  if (!element) return undefined;
  const value = Number(getComputedStyle(element, property));
  return isNaN(value) ? undefined : value;
}

export type TrayState = ReturnType<typeof useTrayState>;

export function useTrayState(props: OverlayTriggerProps): OverlayTriggerState {
  const contextState = useContext(OverlayTriggerStateContext);
  const localState = useOverlayTriggerState(props);

  if (props.isOpen != null || props.defaultOpen != null || !contextState) {
    return localState;
  }

  return contextState;
}

function useHeightObserver(
  ref: RefObject<HTMLElement | null>,
  options: { isDisabled: boolean }
) {
  const [height, setHeight] = useState<number | undefined>(undefined);
  const onResize = useMemo(() => {
    const cb = () => {
      const softKeyboard = isKeyboardInput(document.activeElement);
      if (options.isDisabled || softKeyboard) return;
      setHeight(ref.current?.offsetHeight);
    };
    return debounce(cb, 300);
  }, [ref, options.isDisabled]);

  useResizeObserver({ ref, onResize });
  useLayoutEffect(onResize, [onResize]);

  return height;
}

// Styled components -----------------------------------------------------------

function DragAffordance() {
  const stroke = 4;
  const width = 36;

  return (
    <div
      className={cn(
        'absolute inset-x-0 top-0 z-10 flex flex-col items-center justify-center py-1.5',
        // extend slightly above/outside for larger touch target
        'before:-mt-4 before:h-4 before:w-full'
      )}
    >
      <svg
        height={stroke}
        width={width}
        viewBox={`0 0 ${width} ${stroke}`}
        strokeWidth={stroke}
        strokeLinecap="round"
        className="stroke-placeholder"
      >
        <line
          x1={stroke / 2}
          x2={width - stroke / 2}
          y1={stroke / 2}
          y2={stroke / 2}
        />
      </svg>
    </div>
  );
}
